import * as wellknown from "wellknown";
import { EntityItem, MediaItem } from "../types";
import { getMediaThumbnailSync } from "./authMediaAdapters";
import { constructMediaUrl } from "./mediaUtils";

type FlexibleMediaItem =
  | File
  | (MediaItem & {
      file?: File;
    });

function createFileKey(file: File) {
  return `${file?.name}_${file?.size}_${file?.lastModified}`;
}

export function filterUniqueItems(files: FlexibleMediaItem[]) {
  const map = new Map<string, File | MediaItem>();

  for (const file of files) {
    if (file instanceof File || file?.file) {
      let key;
      {
        if (file instanceof File) {
          key = createFileKey(file);
        } else if (file?.file) {
          key = createFileKey(file.file);
        }
      }

      if (key) {
        if (!map.has(key)) {
          map.set(key, file);
        }
      }
    } else {
      map.set(file?.id?.toString(), file);
    }
  }
  return Array.from(map.values());
}

export function convertFileToMediaData(file: File) {
  const previewUrl = URL.createObjectURL(file);
  return {
    id: Date.now(),
    name: file.name,
    entity_name: file.name,
    entity_thumbnail: previewUrl,
    entity_description: "Associated",
    is_primary: false,
    file
  };
}

// GeoJSON geometry types accepted for each CM table spatial type. Multi* spatial
// types also accept their singular form, since users may draw a single shape.
const SPATIAL_TYPE_GEOMETRY_TYPES: Record<string, string[]> = {
  point: ["Point"],
  multipoint: ["Point", "MultiPoint"],
  polygon: ["Polygon"],
  multipolygon: ["Polygon", "MultiPolygon"],
  linestring: ["LineString"],
  multilinestring: ["LineString", "MultiLineString"]
};

/**
 * Checks whether a GeoJSON geometry type is valid for a CM table's spatial type.
 * Returns null if valid, or an error message describing the mismatch.
 */
export function getGeometryTypeMismatchError(geometryType: string, spatialType: string): string | null {
  const allowedTypes = SPATIAL_TYPE_GEOMETRY_TYPES[spatialType];
  if (!allowedTypes || allowedTypes.includes(geometryType)) return null;
  return `This table only accepts ${allowedTypes.join(" or ")} geometry, but the drawn/entered geometry is ${geometryType}.`;
}

export function getGeometryValidationHelperText(geometry: string | undefined) {
  if (!geometry) return "Supports 2D/3D geometries, Features, and FeatureCollections (Z values auto-stripped)";
  try {
    const geom = JSON.parse(geometry);

    // Handle FeatureCollection
    if (geom.type === "FeatureCollection") {
      if (geom.features && Array.isArray(geom.features)) {
        const featureCount = geom.features.length;
        if (featureCount === 1) {
          return `✓ FeatureCollection with 1 feature (will extract ${geom.features[0].geometry?.type || "unknown"}, Z-stripped)`;
        } else if (featureCount > 1) {
          // Check if we can combine into a Multi* type
          const allTypes = geom.features.map((f: any) => f.geometry?.type).filter((type: any): type is string => typeof type === "string");
          const geometryTypes = [...new Set(allTypes)];
          if (geometryTypes.length === 1) {
            const singleType = geometryTypes[0] as string;
            if (["LineString", "Polygon", "Point"].includes(singleType)) {
              return `✓ FeatureCollection with ${featureCount} ${singleType}s (will combine as Multi${singleType}, Z-stripped)`;
            } else if (["MultiLineString", "MultiPolygon", "MultiPoint"].includes(singleType)) {
              return `✓ FeatureCollection with ${featureCount} ${singleType}s (will flatten into single ${singleType}, Z-stripped)`;
            }
          }
          return `✓ FeatureCollection with ${featureCount} features (will combine as GeometryCollection, Z-stripped)`;
        } else {
          return "⚠ FeatureCollection is empty";
        }
      } else {
        return "⚠ FeatureCollection missing 'features' array";
      }
    }

    // Handle Feature
    if (geom.type === "Feature") {
      if (geom.geometry && geom.geometry.type) {
        return `✓ Valid Feature containing ${geom.geometry.type}`;
      } else {
        return "⚠ Feature missing 'geometry' property";
      }
    }

    // Handle basic geometries
    if (geom.type && geom.coordinates) {
      const supportedTypes = ["Point", "LineString", "Polygon", "MultiLineString", "MultiPolygon"];
      if (supportedTypes.includes(geom.type)) {
        return `✓ Valid ${geom.type} geometry`;
      } else {
        return `⚠ Unsupported geometry type: ${geom.type}`;
      }
    } else {
      return "⚠ Missing 'type' or 'coordinates' property";
    }
  } catch {
    return "⚠ Invalid JSON format";
  }
}

export function getGeometryType(geom: string) {
  try {
    return wellknown.parse(geom)?.type ?? "unknown";
  } catch (e) {
    return "unknown";
  }
}

// Extract geometry from existing item (supports points, polygons, and linestrings)

export function extractGeometryFromItem(item: MediaItem | EntityItem): {
  coordinates: [number, number] | null;
  polygon: GeoJSON.Polygon | null;
} {
  console.log("[EntityForm Utility - extractGeometryFromItem] called for:", item.name, item.id);

  const result: {
    coordinates: [number, number] | null;
    polygon: GeoJSON.Polygon | null;
  } = {
    coordinates: null,
    polygon: null
  };

  const itemAny = item as (MediaItem | EntityItem) & { geometry?: string; _geometry?: string; coordinates?: [number, number] };

  const tryExtract = (raw: string | undefined, source: "geometry" | "_geometry") => {
    if (!raw) return;

    try {
      const geojson = typeof raw === "string" ? wellknown.parse(raw) : raw;

      if (!geojson || !geojson?.type) return;

      switch (geojson?.type) {
        case "Point": {
          result.coordinates = [geojson?.coordinates[0], geojson?.coordinates[1]];
          console.log(`[EntityForm Utility - extractGeometryFromItem] Extracted Point from ${source}:`, result.coordinates);
          break;
        }
        case "Polygon":
          result.polygon = geojson as GeoJSON.Polygon;
          break;
        case "MultiPolygon":
          result.polygon = geojson as unknown as GeoJSON.Polygon;
          break;
        case "LineString":
          result.polygon = geojson as unknown as GeoJSON.Polygon;
          break;
        case "MultiLineString": {
          // stored as polygon for display/edit compatibility
          result.polygon = geojson as unknown as GeoJSON.Polygon;
          console.log(`[EntityForm Utility - extractGeometryFromItem] Extracted ${geojson.type} from ${source}:`, result.polygon);
          break;
        }

        default:
          console.warn(`[EntityForm Utility - extractGeometryFromItem] Unsupported geometry type from ${source}:`, geojson.type);
      }
    } catch (error) {
      console.warn(`[EntityForm Utility - extractGeometryFromItem] Error parsing ${source}:`, error);
    }
  };

  console.log("[EntityForm Utility - extractGeometryFromItem] geometry:", itemAny.geometry);
  console.log("[EntityForm Utility - extractGeometryFromItem] _geometry:", itemAny._geometry);

  // try geometry first
  tryExtract(itemAny.geometry, "geometry");

  // fallback to _geometry only if nothing found
  if (!result.coordinates && !result.polygon) {
    tryExtract(itemAny._geometry, "_geometry");
  }

  if (!result.coordinates && !result.polygon) {
    console.log("[EntityForm Utility - extractGeometryFromItem] No geometry found in item");
  }

  return result;
}

export function sortMediaByPrimaryImage(media: Record<string, unknown>[]) {
  return Array.from(media ?? []).sort((a, b) => {
    if (a?.is_primary && !b?.is_primary) return -1;
    if (!a?.is_primary && b?.is_primary) return 1;
    return 0; // keep original order for others
  });
}

// Helper function to check if an item has relationships
export function hasRelationships(
  relationships:
    | {
        related_entities?: Record<string, any[]>;
        geo_entities?: Record<string, any[]>;
        media?: any[];
      }
    | undefined
): boolean {
  if (!relationships) return false;

  // Check related_entities
  if (relationships.related_entities && Object.values(relationships.related_entities).some((arr) => Array.isArray(arr) && arr.length > 0)) {
    return true;
  }

  // Check geo_entities
  if (relationships.geo_entities && Object.values(relationships.geo_entities).some((arr) => Array.isArray(arr) && arr.length > 0)) {
    return true;
  }

  // Check media
  if (Array.isArray(relationships.media) && relationships.media.length > 0) {
    return true;
  }

  return false;
}

// Helper function to get thumbnail URL for relationship media items
export const getRelationshipMediaThumbnailUrl = (mediaItem: any): string => {
  // For relationship media items, we need to construct the proper item structure
  // since extractMediaItems only gives us limited properties
  const itemForThumbnail = {
    id: mediaItem.id,
    entity_thumbnail: mediaItem.entity_thumbnail,
    // Add any other available properties for the adapter pattern
    thumbnail: mediaItem.thumbnail,
    url: mediaItem.url,
    primary_image: mediaItem.primary_image,
    remote_details: mediaItem.remote_details,
    // Copy over any Fulcrum-specific data if present
    fulcrum_photo: mediaItem.fulcrum_photo,
    fulcrum_data: mediaItem.fulcrum_data,
    _source: mediaItem._source
  };

  // Use the new Media Resource Adapter pattern
  const thumbnailUrl = getMediaThumbnailSync(itemForThumbnail);

  // If that fails, fall back to API endpoint
  if (!thumbnailUrl && mediaItem.id) {
    return constructMediaUrl(`/api/user/media/${mediaItem.id}/thumbnail/`);
  }

  return thumbnailUrl || "";
};
